/**
 * Shared types + helpers for the V1 date-range filter (ported from ads-iq).
 *
 * Presets map to backend windows; "custom" requires explicit start/end.
 * The URL is the source of truth (see useDateRangeState) so filtered
 * views are shareable and survive a page reload.
 *
 * 2026-05-04: Compare modes now match ads-iq verbatim. The 5 modes are
 * (off | prior_period | prior_year | prior_year_dow | custom). The backend
 * `_resolve_compare_window` (app/api/performance.py) honors ALL of them —
 * prior_year = -365d, prior_year_dow = -364d, custom = explicit dates,
 * prior_period = preceding same-length span. (Verified 2026-06-21; an earlier
 * note here wrongly claimed prior_year fell back to prior_period — that was
 * stale.)
 *
 * 2026-06-21: rolling presets support an `includeToday` flag (matches ads-iq).
 * Off by default (today's partial, not-yet-synced data is excluded). When on,
 * dateRangeQs sends the resolved today-ending window as explicit custom dates,
 * so no backend param is needed — the existing custom path produces the window.
 */

/** Compare modes — match ads-iq. "custom" lets the operator pick an explicit
 *  comparison window (compare_start_date / compare_end_date), ported from
 *  ads-iq's "compare with period" feature (2026-06-18). */
export type CompareMode =
  | "off"
  | "prior_period"
  | "prior_year"
  | "prior_year_dow"
  | "custom";

// Default to prior_period because every dashboard page header literally
// says "WoW deltas vs the prior period". Defaulting to "off" hid the
// deltas and made the cards say "No prior period" — a contradiction.
// Users explicitly opt OUT of compare via the dropdown.
export const DEFAULT_COMPARE_MODE: CompareMode = "prior_period";

export type DateRangePreset =
  | "today"
  | "yesterday"
  | "last_7d"
  | "last_30d"
  | "last_90d"
  | "last_180d"
  | "last_365d"
  | "custom";

export const PRESET_LABEL: Record<DateRangePreset, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last_7d: "7D",
  last_30d: "30D",
  last_90d: "3M",
  last_180d: "6M",
  last_365d: "12M",
  custom: "Custom",
};

/** Order pills appear in the UI bar. */
export const PRESET_ORDER: DateRangePreset[] = [
  "custom",
  "today",
  "yesterday",
  "last_7d",
  "last_30d",
  "last_90d",
  "last_180d",
  "last_365d",
];

export interface DateRangeValue {
  preset: DateRangePreset;
  /** YYYY-MM-DD; only meaningful when preset === "custom". */
  startDate?: string;
  endDate?: string;
  /** Rolling presets only (7D/30D/…): end the window TODAY instead of
   *  yesterday. Off by default — today's data isn't fully synced. No-op for
   *  today/yesterday/custom. */
  includeToday?: boolean;
}

// Default open window across every page that uses useDateRangeState.
// retention-iq diverges from ads-iq here: the dashboard hero copy says
// "Last 30 days" so we keep last_30d as the default landing view, with
// 7D / 3M / 6M / 12M / Custom one click away.
export const DEFAULT_RANGE: DateRangeValue = { preset: "last_30d" };

/**
 * Compose the query-string fragment for an API call. Returns the
 * fragment WITHOUT a leading "&" so callers compose freely.
 *
 * Example: dateRangeQs({preset:"custom", startDate:"2026-04-01", endDate:"2026-04-10"})
 *   → "date_range=custom&start_date=2026-04-01&end_date=2026-04-10"
 */
export function dateRangeQs(value: DateRangeValue): string {
  if (value.preset === "custom" && value.startDate && value.endDate) {
    return `date_range=custom&start_date=${encodeURIComponent(value.startDate)}&end_date=${encodeURIComponent(value.endDate)}`;
  }
  // includeToday on a rolling preset → send the resolved TODAY-ending window
  // as explicit dates (the backend lets explicit start/end win over the preset,
  // which would otherwise end yesterday). No-op for today/yesterday.
  if (value.includeToday && value.preset !== "today" && value.preset !== "yesterday") {
    const { start, end } = resolveRangeWindow(value);
    return `date_range=custom&start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}`;
  }
  return `date_range=${encodeURIComponent(value.preset)}`;
}

/** Stable cache key shape for react-query. */
export function dateRangeKey(value: DateRangeValue): string {
  if (value.preset === "custom") {
    return `custom:${value.startDate ?? ""}-${value.endDate ?? ""}`;
  }
  return value.includeToday ? `${value.preset}+today` : value.preset;
}

/** Explicit comparison window — only meaningful when compareMode === "custom". */
export interface CompareValue {
  mode: CompareMode;
  /** YYYY-MM-DD; only used when mode === "custom". */
  startDate?: string;
  endDate?: string;
}

/**
 * Compose the compare query-string fragment (no leading "&"). Emits
 * `compare_mode=X`, plus `compare_start_date`/`compare_end_date` when the
 * mode is "custom" and both dates are set. Returns "" for compareMode "off"
 * so callers can skip it. Ported from ads-iq compareQs (2026-06-18).
 */
export function compareQs(
  mode: CompareMode,
  compareStart?: string,
  compareEnd?: string,
): string {
  if (mode === "off") return "";
  if (mode === "custom" && compareStart && compareEnd) {
    return `compare_mode=custom&compare_start_date=${encodeURIComponent(compareStart)}&compare_end_date=${encodeURIComponent(compareEnd)}`;
  }
  return `compare_mode=${encodeURIComponent(mode)}`;
}

/** Stable cache-key fragment for compare state (react-query). */
export function compareKey(value: CompareValue): string {
  if (value.mode === "custom") {
    return `custom:${value.startDate ?? ""}-${value.endDate ?? ""}`;
  }
  return value.mode;
}

/** Default custom-comparison window: the 30 days immediately before the
 *  default custom range (i.e. days 31–60 ago). A sensible starting pair the
 *  operator can adjust. */
export function defaultCompareStart(): string {
  const d = new Date();
  d.setDate(d.getDate() - 60);
  return d.toISOString().slice(0, 10);
}
export function defaultCompareEnd(): string {
  const d = new Date();
  d.setDate(d.getDate() - 31);
  return d.toISOString().slice(0, 10);
}

/**
 * Resolve a preset to its ABSOLUTE inclusive window (UTC), mirroring the
 * backend date-range parsing: rolling presets END yesterday (today's partial,
 * not-yet-synced data is excluded); today/yesterday are 1-day windows; custom
 * uses its own dates. Used by the unified picker to seed the current-range
 * date inputs + dual-highlight calendar from a preset. UTC so it matches the
 * data. Rolling presets end yesterday by default; with `includeToday` they end
 * today (matches ads-iq).
 *
 * Ported from ads-iq for the unified comparison picker (2026-06-21).
 */
export function resolveRangeWindow(value: DateRangeValue): { start: string; end: string } {
  const isoUTC = (d: Date) => d.toISOString().slice(0, 10);
  if (value.preset === "custom" && value.startDate && value.endDate) {
    return { start: value.startDate, end: value.endDate };
  }
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (value.preset === "today") return { start: isoUTC(today), end: isoUTC(today) };
  const yesterday = new Date(today);
  yesterday.setUTCDate(today.getUTCDate() - 1);
  if (value.preset === "yesterday") return { start: isoUTC(yesterday), end: isoUTC(yesterday) };
  const days = presetToDays(value);
  // includeToday → window ends TODAY; otherwise yesterday (default — today's
  // partial, not-yet-synced data is excluded).
  const end = value.includeToday ? today : yesterday;
  const start = new Date(end);
  start.setUTCDate(end.getUTCDate() - (days - 1));
  return { start: isoUTC(start), end: isoUTC(end) };
}

/**
 * Human label for a preset's resolved window — "May 27 – Jun 2", or a single
 * "Jun 2" for today/yesterday. Ported from ads-iq for the unified comparison
 * picker (2026-06-21).
 */
export function formatRangeWindow(value: DateRangeValue): string {
  const { start, end } = resolveRangeWindow(value);
  const fmt = (s: string) =>
    new Date(`${s}T00:00:00Z`).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  return start === end ? fmt(start) : `${fmt(start)} – ${fmt(end)}`;
}

/** Format a YYYY-MM-DD or Date as "Apr 25". */
export function formatDateShort(input: string | Date): string {
  const d = typeof input === "string" ? new Date(input + "T00:00:00") : input;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

/** Default custom-range start: 30 days ago. */
export function defaultCustomStart(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

/** Default custom-range end: yesterday. */
export function defaultCustomEnd(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Number of days a preset represents. Used by callers that still need to
 * pass `days=N` to legacy endpoints / components (e.g. HeroKpiCard's
 * `periodDays` prop). For "custom", returns the inclusive day count.
 *
 * Mirrors backend `_resolve_window` logic in app/api/performance.py.
 */
export function presetToDays(value: DateRangeValue): number {
  if (value.preset === "custom" && value.startDate && value.endDate) {
    const start = new Date(value.startDate + "T00:00:00");
    const end = new Date(value.endDate + "T00:00:00");
    const ms = end.getTime() - start.getTime();
    return Math.max(1, Math.round(ms / 86400000));
  }
  switch (value.preset) {
    case "today":
    case "yesterday":
      return 1;
    case "last_7d":
      return 7;
    case "last_30d":
      return 30;
    case "last_90d":
      return 90;
    case "last_180d":
      return 180;
    case "last_365d":
      return 365;
    default:
      return 30;
  }
}

/**
 * Resolve a DateRangeValue into the {from, to} a *catalog* endpoint expects.
 *
 * Catalog backends (product-insights / category-insights) treat `from` as
 * INCLUSIVE and `to` as EXCLUSIVE, with the default window *including today*
 * (`to = today + 1`). See app/api/brands_product_insights.py. This differs
 * from the report-page convention (inclusive end, ending yesterday), so
 * catalog uses this dedicated resolver rather than the generic one — using
 * the report resolver here would silently shift the window back a day and
 * drop today's orders.
 *
 *   today      → [today,      today+1)      = today
 *   yesterday  → [yesterday,  today)        = yesterday
 *   last_Nd    → [today+1-N,  today+1)      = last N days, incl. today
 *   custom     → [startDate,  endDate+1)    = endDate included (exclusive end)
 */
export function resolveCatalogRange(value: DateRangeValue): { from: string; to: string } {
  const isoUTC = (d: Date): string => d.toISOString().slice(0, 10);
  const addDays = (iso: string, n: number): string => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return isoUTC(d);
  };

  if (value.preset === "custom" && value.startDate && value.endDate) {
    // The custom popover gives an inclusive end; backend `to` is exclusive.
    return { from: value.startDate, to: addDays(value.endDate, 1) };
  }

  const today = isoUTC(new Date());
  if (value.preset === "yesterday") {
    return { from: addDays(today, -1), to: today };
  }
  // "today" and every range preset end today (inclusive) → exclusive to = today+1.
  const to = addDays(today, 1);
  return { from: addDays(to, -presetToDays(value)), to };
}
